import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RunKudosDto } from '@rw/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { Avatar } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { MY_COLOR } from '../../lib/colors';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';

export const kudosKeys = { run: (runId: string) => ['run-kudos', runId] as const };
const KUDOS = '🏃‍♂️';

/** Strava-style kudos, with a runner instead of a heart. */
export function RunKudos({ runId, isOwner }: { runId: string; isOwner: boolean }) {
  const me = useAuth((s) => s.user?.id);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const t = useT();
  const key = kudosKeys.run(runId);
  const q = useQuery({ queryKey: key, queryFn: () => api<RunKudosDto>(`/runs/${runId}/kudos`), refetchInterval: 60_000 });
  const toggle = useMutation({
    mutationFn: (give: boolean) => api<RunKudosDto>(`/runs/${runId}/kudos`, { method: give ? 'POST' : 'DELETE' }),
    onMutate: (give) => {
      const prev = qc.getQueryData<RunKudosDto>(key);
      if (prev) qc.setQueryData<RunKudosDto>(key, { ...prev, kudoedByMe: give, count: prev.count + (give ? 1 : -1) });
      return { prev };
    },
    onSuccess: (data) => qc.setQueryData(key, data),
    onError: (e, _v, c) => {
      if (c?.prev) qc.setQueryData(key, c.prev);
      toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Could not give kudos') });
    },
  });
  const k = q.data;
  const count = k?.count ?? 0;

  return (
    <div className="mt-6 flex items-center gap-3" data-testid="run-kudos">
      {isOwner ? null : (
        <button
          onClick={() => toggle.mutate(!k?.kudoedByMe)}
          disabled={!k}
          aria-pressed={!!k?.kudoedByMe}
          data-testid="kudos-button"
          className={`flex h-11 items-center gap-2 rounded-full border px-4 font-display text-sm font-bold uppercase transition active:scale-95 ${
            k?.kudoedByMe ? 'border-volt bg-volt/15 text-volt' : 'border-ink-600 bg-ink-850 text-ink-200'
          }`}
        >
          <span className={`text-xl leading-none ${k?.kudoedByMe ? '' : 'grayscale'}`}>{KUDOS}</span>
          {t(k?.kudoedByMe ? 'Kudos given' : 'Give kudos')}
        </button>
      )}
      <button onClick={() => count && setOpen(true)} className="flex min-w-0 items-center gap-2 text-sm text-ink-300" data-testid="kudos-count">
        {k?.users.length ? (
          <span className="flex -space-x-2">
            {k.users.slice(0, 4).map((u) => (
              <Avatar key={u.id} name={u.displayName} color={u.id === me ? MY_COLOR : u.color} src={u.avatarUrl} size="sm" />
            ))}
          </span>
        ) : null}
        <span>
          {KUDOS} <b className="text-ink-100">{count}</b> {t(count === 1 ? 'kudo' : 'kudos')}
        </span>
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} label={t('Kudos')}>
        <div className="font-display text-2xl font-bold">
          {KUDOS} {count} {t(count === 1 ? 'kudo' : 'kudos')}
        </div>
        <ul className="mt-3 space-y-1" data-testid="kudos-list">
          {k?.users.map((u) => (
            <li key={u.id}>
              <Link to={u.id === me ? '/profile' : `/players/${u.id}`} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-ink-800">
                <Avatar name={u.displayName} color={u.id === me ? MY_COLOR : u.color} src={u.avatarUrl} size="sm" />
                <span className="font-semibold">{u.displayName}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Sheet>
    </div>
  );
}
